/* eslint-disable no-param-reassign, max-lines */
import { api } from "packages/jiffy-api";
import { simpleSnakeCase } from "common/utils/helpers/cases";
import createTimerManager from "common/utils/delivery-date/make-timer-manager";
import {
  extractDayFromDate,
  shouldShowTimeRange,
  formatDeliveryDateForDisplay
} from "common/utils/format/date";

export class DeliveryDateClass {
  #deliverySelector = "[data-delivery-for]";

  #cutoffDataKey = "deliveryCutoffSelector";

  #loadingDataKey = "deliveryLoadingSelector";

  #orderByDataKey = "deliveryOrderBySelector";

  #deliveryNewLookSelector = ".js-delivery-section-new-look";

  #timerManager;

  constructor() {
    this.#timerManager = createTimerManager();
  }

  get #businessLines() {
    const businessLines = new Set();
    this.#$deliveryElements.forEach($content => {
      businessLines.add($content.dataset.deliveryFor);
    });
    return businessLines;
  }

  get #$deliveryElements() {
    return document.querySelectorAll(this.#deliverySelector);
  }

  get #$deliveryNewLookSection() {
    return document.querySelector(this.#deliveryNewLookSelector);
  }

  static get zipcode() {
    return document.documentElement.dataset.zipcode;
  }

  static #isElementForBusinessLine($element, businessLine) {
    return $element.dataset.deliveryFor === businessLine;
  }

  static #$getElement($element, selectorType) {
    const selector = $element.dataset[selectorType];
    return selector ? document.querySelector(selector) : null;
  }

  #setContent(content, businessLine) {
    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        if (this.#$deliveryNewLookSection?.contains($element)) {
          $element.textContent = `Get it by ${content}`;
        } else {
          $element.textContent = content;
        }
        $element.classList.remove("min-w-30");
      }
    });
  }

  #setError(businessLine) {
    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        const errorMessage = $element.dataset.errorMessage || "...";
        $element.textContent = errorMessage;

        const $cutoffNode = DeliveryDateClass.#$getElement($element, this.#cutoffDataKey);
        if ($cutoffNode) {
          $cutoffNode.parentElement.classList.add("hidden");
        }
      }
    });
  }

  #toggleLoading(businessLine, isLoading) {
    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        const $loadingNode = DeliveryDateClass.#$getElement($element, this.#loadingDataKey);
        if ($loadingNode) {
          $loadingNode.classList.toggle("hidden", !isLoading);
          $element.classList.toggle("hidden", isLoading);
        }
      }
    });
  }

  async #updateLocation() {
    const businessLines = this.#businessLines;
    this.#businessLines.forEach(businessLine => {
      this.#toggleLoading(businessLine, true);
    });
    const response = await api.location.update({
      zipcode: DeliveryDateClass.zipcode
    });

    // Dispatch location data event for other components
    document.dispatchEvent(
      new CustomEvent("location-data-updated", {
        detail: {
          zipcode: DeliveryDateClass.zipcode,
          locationData: response
        }
      })
    );

    if (response.error) {
      businessLines.forEach(businessLine => {
        this.#setError(businessLine);
        this.#toggleLoading(businessLine, false);
      });
      throw response.error;
    }
  }

  #setCutoffTimer(cutoffTime, businessLine, isExpedite = false) {
    if (!cutoffTime) return;

    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        const $cutoffNode = DeliveryDateClass.#$getElement($element, this.#cutoffDataKey);
        if (!$cutoffNode) return;

        const cutoffTimestamp = new Date(cutoffTime).getTime();
        const $parent = $cutoffNode.parentElement;
        $parent.classList.remove("hidden");

        const $expediteNote = $parent.querySelector(".js-expedite-shipping-note");
        if ($expediteNote) {
          if (isExpedite) {
            $expediteNote.textContent = ", with expedite shipping";
            $parent.setAttribute("data-expedite-shipping", "true");
          } else {
            $expediteNote.textContent = "";
            $parent.removeAttribute("data-expedite-shipping");
          }
        }

        this.#timerManager.makeTimer($cutoffNode, cutoffTimestamp);
      }
    });
  }

  #setOrderByText(htmlContent, businessLine, isExpedite = false) {
    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        const $orderByNode = DeliveryDateClass.#$getElement($element, this.#orderByDataKey);
        if ($orderByNode) {
          let displayContent = htmlContent;
          if (isExpedite) {
            displayContent += ", with expedite shipping";
          }
          $orderByNode.innerHTML = displayContent;
          const shouldShowOrderBy = $element.dataset.showOrderBy === "true";

          if (shouldShowOrderBy) {
            $orderByNode.classList.remove("hidden");

            const $cutoffNode = DeliveryDateClass.#$getElement($element, this.#cutoffDataKey);
            if ($cutoffNode && $cutoffNode.parentElement) {
              $cutoffNode.parentElement.classList.add("hidden");
            }
          }
        }
      }
    });
  }

  /** @abstract */
  setShipDayStatus() {
    return this;
  }

  #updateDynamicLabels(deliveryType, businessLine) {
    this.#$deliveryElements.forEach($element => {
      if (DeliveryDateClass.#isElementForBusinessLine($element, businessLine)) {
        const labelSelector = $element.dataset.deliveryLabelSelector;
        const $label = labelSelector ? document.querySelector(labelSelector) : null;

        if ($label) {
          const $deliveryTypeSpan = $label.querySelector(".js-location-delivery-type");
          const isStandardDelivery = deliveryType === "standard";

          if ($deliveryTypeSpan) {
            // LP page with city span structure - only update the delivery type
            $deliveryTypeSpan.textContent = isStandardDelivery ? "Standard" : "Local";
          } else {
            // Regular page - update entire label
            const regionalLabel = $element.dataset.regionalDeliveryLabel;
            const defaultLabel = isStandardDelivery ? "Standard Delivery" : "Local Delivery";
            $label.textContent = regionalLabel || defaultLabel;
          }
        }
      }
    });
  }

  static #isExpediteDelivery(deliveryData) {
    const deliveryDate = deliveryData?.date?.toLowerCase() || "";
    return deliveryDate.includes("today");
  }

  static #isFastDeliveryEstimateEnabled() {
    return document.documentElement.dataset.fastDeliveryEstimate === "true";
  }

  static #shouldUseFastEta(businessLine) {
    return (
      DeliveryDateClass.#isFastDeliveryEstimateEnabled() &&
      (businessLine === "Blanks" ||
        businessLine === "Transfers" ||
        businessLine === "Print" ||
        businessLine === "Stickers")
    );
  }

  static #fetchEstimates(businessLine) {
    const params = { business_line: simpleSnakeCase(businessLine) };
    return DeliveryDateClass.#shouldUseFastEta(businessLine)
      ? api.product.fetchBusinessLineEta(params)
      : api.product.fetchBusinessDeliveryDates(params);
  }

  async #fetchDeliveryDate(businessLine) {
    try {
      const {
        composition_ids: compositionIds,
        estimates,
        error,
        success
      } = await DeliveryDateClass.#fetchEstimates(businessLine);

      if (!success) {
        throw new Error(error);
      }

      const firstCompositionId = compositionIds[0];
      const deliveryData = estimates[firstCompositionId];
      const { delivery_time: deliveryTime, delivery_time_end: deliveryTimeEnd } = deliveryData;
      const isTimeRangeAvailable =
        shouldShowTimeRange(deliveryData.date) && deliveryTime && deliveryTimeEnd;
      const isExpedite = DeliveryDateClass.#isExpediteDelivery(deliveryData);

      this.#updateDynamicLabels(deliveryData.delivery_type, businessLine);

      if (isTimeRangeAvailable) {
        const formattedDate = extractDayFromDate(deliveryData.date);
        this.#setContent(formattedDate, businessLine);
        if (deliveryData.cutoff_label) {
          this.#setOrderByText(
            `Order by <strong>${deliveryData.cutoff_label}</strong>`,
            businessLine,
            isExpedite
          );
        }
      } else {
        this.#setContent(formatDeliveryDateForDisplay(deliveryData.date), businessLine);
        this.#setCutoffTimer(deliveryData.timeleft, businessLine, isExpedite);
        this.setShipDayStatus(deliveryData.one_ship_day, deliveryData.date, businessLine);
      }
    } catch (error) {
      this.#setError(businessLine);
    } finally {
      this.#toggleLoading(businessLine, false);
    }
  }

  async init(onLocationUpdateSuccess) {
    this.#timerManager.clearTimers();

    await this.#updateLocation();

    if (onLocationUpdateSuccess) {
      onLocationUpdateSuccess();
    }

    this.#businessLines.forEach(businessLine => {
      this.#fetchDeliveryDate(businessLine);
    });
  }
}

export default () => new DeliveryDateClass();
